// src/components/rcv/ObtencionTokenTab.jsx - Componente renombrado desde AuthenticationTab
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setTokenSII } from '../../redux/authSlice';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Typography,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Tooltip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel
} from '@mui/material';
import {
  Key,
  Upload,
  ErrorOutline,
  Visibility,
  VisibilityOff,
  LockOutlined,
  VpnKeyOutlined,
  Business,
  Warning
} from '@mui/icons-material';
import { siiService } from '../../services/siiService';

// Componente Tab Panel para las pestañas de autenticación
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ObtencionTokenTab = () => {
  const navigate = useNavigate();
  const [authMethod, setAuthMethod] = useState(0); // 0: cert empresa, 1: gestor certificados, 2: manual, 3: certificado
  const [manualToken, setManualToken] = useState('');
  const [certificate, setCertificate] = useState(null);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [certificadosLoading, setCertificadosLoading] = useState(false);
  
  // Certificados del gestor del sistema
  const [certificados, setCertificados] = useState([]);
  const [certificadoSeleccionado, setCertificadoSeleccionado] = useState(null);
  
  // Certificados de empresa
  const [certificadosEmpresa, setCertificadosEmpresa] = useState([]);
  const [certificadoEmpresaSeleccionado, setCertificadoEmpresaSeleccionado] = useState(null);
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const { empresaActiva } = useSelector((state) => state.empresa);
  const { isAuthenticated } = useSelector((state) => state.auth);

  // Definir funciones con useCallback para corregir dependencias
  const cargarCertificados = useCallback(async () => {
    try {
      setCertificadosLoading(true);
      setError('');
        
      // Obtener certificados disponibles desde el backend
      const certificadosDisponibles = await siiService.getCertificadosDisponibles();
        
      if (certificadosDisponibles && certificadosDisponibles.length > 0) {
        setCertificados(certificadosDisponibles);
        // Seleccionar el primer certificado por defecto
        setCertificadoSeleccionado(certificadosDisponibles[0]);
      } else {
        setError('No hay certificados configurados en el servidor');
      }
    } catch (err) {
      setError('Error al cargar certificados disponibles: ' + (err.message || 'Error desconocido'));
    } finally {
      setCertificadosLoading(false);
    }
  }, [setCertificadosLoading, setError, setCertificados, setCertificadoSeleccionado]);
  
  const cargarCertificadosEmpresa = useCallback(async () => {
    if (!empresaActiva) {
      setError('No hay una empresa seleccionada');
      setCertificadosEmpresa([]);
      setCertificadoEmpresaSeleccionado(null);
      return;
    }

    try {
      setCertificadosLoading(true);
      setError('');
      
      // Obtener certificados de la empresa activa
      const certs = await siiService.getCertificadosEmpresa();
      
      if (certs && certs.length > 0) {
        setCertificadosEmpresa(certs);
        // Seleccionar el primer certificado por defecto
        setCertificadoEmpresaSeleccionado(certs[0]);
      } else {
        setError(`No hay certificados configurados para la empresa ${empresaActiva.nombre_corto || empresaActiva.razon_social}`);
      }
    } catch (err) {
      setError('Error al cargar certificados de la empresa: ' + (err.message || 'Error desconocido'));
    } finally {
      setCertificadosLoading(false);
    }
  }, [empresaActiva, setCertificadosLoading, setError, setCertificadosEmpresa, setCertificadoEmpresaSeleccionado]);
  
  // Cargar certificados disponibles al inicio
  useEffect(() => {
    if (authMethod === 0) {
      cargarCertificadosEmpresa();
    } else if (authMethod === 1) {
      cargarCertificados();
    }
  }, [authMethod, cargarCertificados, cargarCertificadosEmpresa]);

  // Cuando cambia la empresa activa, cargar sus certificados
  useEffect(() => {
    if (authMethod === 0 && empresaActiva) {
      cargarCertificadosEmpresa();
    }
  }, [empresaActiva, authMethod, cargarCertificadosEmpresa]);

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setCertificate(e.target.files[0]);
    }
  };

  const handleManualTokenSubmit = (e) => {
    e.preventDefault();
    if (manualToken.trim()) {
      dispatch(setTokenSII(manualToken.trim()));
      setError('');
      setSuccess('Token SII configurado correctamente. Ya puede consultar el RCV.');
      setTimeout(() => {
        navigate('/rcv/consulta');
      }, 1500);
    } else {
      setError('El token no puede estar vacío');
    }
  };

  const handleCertificadoEmpresaSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!certificadoEmpresaSeleccionado) {
      setError('Debe seleccionar un certificado');
      setLoading(false);
      return;
    }

    try {
      // Autenticar usando el certificado seleccionado de la empresa
      const token = await siiService.authenticateWithEmpresaCertificado(
        certificadoEmpresaSeleccionado.id.toString()
      );
      if (!token) {
        throw new Error("Token recibido es null o vacío");
      }
      dispatch(setTokenSII(token));
      setSuccess('Token SII obtenido correctamente mediante certificado de empresa');
      setTimeout(() => {
        navigate('/rcv/consulta');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Error en el proceso de obtención del token SII');
    } finally {
      setLoading(false);
    }
  };

  const handleCertificadoGestorSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!certificadoSeleccionado) {
      setError('Debe seleccionar un certificado');
      setLoading(false);
      return;
    }

    try {
      // Autenticar usando el certificado seleccionado del gestor
      const token = await siiService.authenticateWithCertificateManager(
        certificadoSeleccionado.id.toString()
      );
      if (!token) {
        throw new Error("Token recibido es null o vacío");
      }
      dispatch(setTokenSII(token));
      setSuccess('Token SII obtenido correctamente mediante certificado del sistema');
      setTimeout(() => {
        navigate('/rcv/consulta');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Error en el proceso de obtención del token SII');
    } finally {
      setLoading(false);
    }
  };

  const handleCertificateSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!certificate) {
      setError('Debe seleccionar un certificado');
      setLoading(false);
      return;
    }

    if (!password) {
      setError('Debe ingresar la contraseña del certificado');
      setLoading(false);
      return;
    }

    try {
      const token = await siiService.authenticate(certificate, password);
      if (!token) {
        throw new Error("Token recibido es null o vacío");
      }
      dispatch(setTokenSII(token));
      setSuccess('Token SII obtenido correctamente mediante certificado digital');
      setTimeout(() => {
        navigate('/rcv/consulta');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Error en el proceso de obtención del token SII');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthMethodChange = (event, newValue) => {
    setAuthMethod(newValue);
  };

  const handleToggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Card>
      <CardHeader 
        title="Obtención de Token SII" 
        subheader={isAuthenticated ? 
          "Token SII activo. Puede consultar el RCV" : 
          "Obtenga un token del SII para acceder a los datos de RCV"
        }
        sx={{ pb: 0 }}
      />
      <CardContent>
        {isAuthenticated && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="subtitle2">
              Token SII activo y operativo
            </Typography>
            <Typography variant="body2">
              Ya puede consultar el RCV directamente desde el SII o cargar datos desde un archivo CSV.
            </Typography>
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Sobre esta funcionalidad:
          </Typography>
          <Typography variant="body2" paragraph>
            Esta pantalla le permite obtener un token técnico del SII para consultar datos del RCV.
            No es un inicio de sesión en el sistema, sino un paso técnico necesario para acceder a la API del SII.
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            <strong>Nota:</strong> El token SII es válido por un tiempo limitado (aproximadamente 1 hora).
          </Typography>
        </Box>

        <Tabs
          value={authMethod}
          onChange={handleAuthMethodChange}
          aria-label="authentication method tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          <Tab label="Certificado de Empresa" id="auth-tab-0" aria-controls="auth-tabpanel-0" />
          <Tab label="Gestor de Certificados" id="auth-tab-1" aria-controls="auth-tabpanel-1" />
          <Tab label="Token Manual" id="auth-tab-2" aria-controls="auth-tabpanel-2" />
          <Tab label="Certificado Digital" id="auth-tab-3" aria-controls="auth-tabpanel-3" />
        </Tabs>

        {/* Tab de Certificados de Empresa */}
        <TabPanel value={authMethod} index={0}>
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            {!empresaActiva ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No hay una empresa seleccionada. Por favor, seleccione una empresa desde el menú superior.
              </Alert>
            ) : (
              <>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Certificados de {empresaActiva.nombre_corto || empresaActiva.razon_social} ({empresaActiva.rut}-{empresaActiva.dv})
                </Typography>
                
                {certificadosLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : certificadosEmpresa.length > 0 ? (
                  <Box sx={{ mb: 3 }}>
                    <FormControl component="fieldset">
                      <RadioGroup 
                        aria-label="certificados" 
                        name="certificados"
                        value={certificadoEmpresaSeleccionado ? certificadoEmpresaSeleccionado.id.toString() : ''}
                        onChange={(e) => {
                          const certId = parseInt(e.target.value);
                          const cert = certificadosEmpresa.find(c => c.id === certId);
                          setCertificadoEmpresaSeleccionado(cert);
                        }}
                      >
                        {certificadosEmpresa.map((cert) => (
                          <FormControlLabel 
                            key={cert.id}
                            value={cert.id.toString()}
                            control={<Radio />}
                            label={
                              <Box>
                                <Typography variant="body1">{cert.nombre}</Typography>
                                {cert.fecha_expiracion && (
                                  <Typography variant="caption" color="text.secondary">
                                    Expira: {new Date(cert.fecha_expiracion).toLocaleDateString()}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No hay certificados configurados para esta empresa. 
                    Agregue certificados en la sección de empresas.
                  </Alert>
                )}
                
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleCertificadoEmpresaSubmit}
                  disabled={loading || !certificadoEmpresaSeleccionado}
                  startIcon={loading ? <CircularProgress size={20} /> : <Business />}
                >
                  {loading ? 'Obteniendo token...' : 'Obtener Token SII'}
                </Button>
              </>
            )}
          </Paper>
        </TabPanel>

        {/* Tab de Gestor de Certificados */}
        <TabPanel value={authMethod} index={1}>
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Certificados Disponibles en el Sistema
            </Typography>
            
            {certificadosLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : certificados.length > 0 ? (
              <Box sx={{ mb: 3 }}>
                {certificados.map((cert) => (
                  <Box 
                    key={cert.id}
                    sx={{ 
                      p: 2,
                      mb: 1,
                      border: '1px solid',
                      borderColor: certificadoSeleccionado?.id === cert.id ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: certificadoSeleccionado?.id === cert.id ? 'action.selected' : 'background.paper'
                    }}
                    onClick={() => setCertificadoSeleccionado(cert)}
                  >
                    <VpnKeyOutlined color="primary" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        {cert.name || `Certificado ${cert.id}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {cert.description || 'Sin descripción'}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                No hay certificados configurados en el sistema.
              </Alert>
            )}
            
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleCertificadoGestorSubmit}
              disabled={loading || !certificadoSeleccionado}
              startIcon={loading ? <CircularProgress size={20} /> : <LockOutlined />}
            >
              {loading ? 'Obteniendo token...' : 'Obtener Token SII'}
            </Button>
            
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Esta opción utiliza certificados configurados en el servidor para conectarse con el SII.
            </Typography>
          </Paper>
        </TabPanel>

        {/* Tab de Token Manual */}
        <TabPanel value={authMethod} index={2}>
          <form onSubmit={handleManualTokenSubmit}>
            <TextField
              fullWidth
              id="token"
              label="Token SII"
              placeholder="Ingrese el token obtenido manualmente del SII"
              variant="outlined"
              margin="normal"
              value={manualToken}
              onChange={(e) => setManualToken(e.target.value)}
              helperText="Ingrese un token obtenido externamente desde el portal del SII"
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Key />}
            >
              {loading ? 'Procesando...' : 'Usar Token SII'}
            </Button>
          </form>
        </TabPanel>

        {/* Tab de Certificado Digital */}
        <TabPanel value={authMethod} index={3}>
          <form onSubmit={handleCertificateSubmit}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Certificado Digital (.pfx)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<Upload />}
                  sx={{ flex: 1 }}
                >
                  {certificate ? certificate.name : 'Seleccionar archivo'}
                  <input
                    type="file"
                    accept=".pfx"
                    hidden
                    onChange={handleFileChange}
                  />
                </Button>
              </Box>
            </Box>

            <TextField
              fullWidth
              id="password"
              label="Contraseña del Certificado"
              variant="outlined"
              margin="normal"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleToggleShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              sx={{ mt: 2 }}
              startIcon={loading ? <CircularProgress size={20} /> : <Key />}
            >
              {loading ? 'Obteniendo token...' : 'Obtener Token SII'}
            </Button>
          </form>
        </TabPanel>

        {error && (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            icon={<ErrorOutline />}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mt: 2 }}
          >
            {success}
          </Alert>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
          * El token SII es válido por un tiempo limitado (aproximadamente 1 hora). Deberá obtener un nuevo token después de este periodo.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default ObtencionTokenTab;